import { action, computed, makeObservable, observable, runInAction } from 'mobx'
import { Id } from 'types/definitions'
import Job, { RatingMethod } from 'jobs/Job'
import { RatingStatus } from './dtos'
import RateProDialogViewModel from './RateProDialog/RateProDialogViewModel'
import JobRatingsStore from './JobRatingStore'
import { AlertFactory } from '../../alerts/Alert'
import { merge, Subscription } from 'rxjs'
import JobProgressionViewModel from '../views/JobRoot/Job/JobProgressionCard/JobProgressionViewModel'
import { toStream } from 'utils/mobx-util'

/**
 * Ratings view model.
 */
export default class RatingsViewModel {
  @observable
  private proRatingStatus = RatingStatus.Unknown

  rateProDialogVM = new RateProDialogViewModel(
    this.job.proChampion,
    this.ratingMethod,
    (rating: number, feedback: string) =>
      this.submitProRating(rating, feedback),
    this.newAlert,
  )

  private proRatingDialogSubscription: Subscription | undefined

  constructor(
    private job: Job,
    private jobRatingStore: JobRatingsStore,
    private newAlert: AlertFactory,
    private jobProgressionVM: JobProgressionViewModel,
  ) {
    makeObservable(this)
  }

  @computed
  get ratingMethod(): RatingMethod {
    return this.isChampionClient
      ? this.job.ratingConfig.customerRatingMethod
      : 'stars'
  }

  @computed
  get isChampionClient() {
    return this.job.isChampionClientCollaborator
  }

  @computed
  get proChampion() {
    return this.job.proChampion
  }

  @computed
  get canRatePro() {
    return this.proRatingStatus === RatingStatus.Pending && !!this.proChampion
  }

  @computed
  get canSeeRatingNotificationBar(): boolean {
    return (
      this.job.isChampionClientCollaborator && this.canRatePro && !this.isAdmin
    )
  }

  @computed
  get isAdmin() {
    return this.job.canTransferJobAsAdmin
  }

  async activate() {
    await this.fetchJobRatingStatus()

    if (this.proRatingStatus !== RatingStatus.NotApplicable) {
      const jobDraftApproved$ = toStream(
        () => this.jobProgressionVM.isCurrentStepStatusDraftApproved,
      )

      this.proRatingDialogSubscription = merge(
        jobDraftApproved$,
        this.job.statusChange$,
      ).subscribe(async () => {
        await this.fetchJobRatingStatus()

        if (this.isChampionClient && !this.isAdmin) {
          this.showRateProDialog()
        }
      })
    }
  }

  deactivate() {
    this.rateProDialogVM.close()
    this.proRatingDialogSubscription?.unsubscribe()
  }

  async fetchJobRatingStatus() {
    const jobRatingStatus = await this.jobRatingStore.getJobRatingStatus(
      Id.assert(this.job.id),
    )

    runInAction(() => {
      this.proRatingStatus = jobRatingStatus.proRating
    })
  }

  async submitProRating(rating: number, feedback: string) {
    await this.jobRatingStore.submitProRating(
      Id.assert(this.job.id),
      rating,
      Id.assert(this.job.proChampion?.member.user.publicId),
      feedback,
    )

    await this.fetchJobRatingStatus()
  }

  @action.bound
  showRateProDialog() {
    if (!this.canRatePro) {
      return
    }

    this.rateProDialogVM.show()
  }
}
